'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex, Skeleton, Image } from '@chakra-ui/react';

import { BannerProps } from '@/types/Banner';
import { api } from '@/services/api';
import Link from 'next/link';
import ArrowButton from '../ArrowButton';

const BannerCarousel = () => {
  const [banners, setBanners] = useState<BannerProps[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentBannerTime, setCurrentBannerTime] = useState(4);
  const [mouseHover, setMouseHover] = useState(false);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const prevSlide = useCallback((index: number, bannersData: BannerProps[]) => {
    const newIndex = index === 0 ? bannersData.length - 1 : index - 1;
    setCurrentIndex(newIndex);
    setCurrentBannerTime(bannersData[newIndex].time);
  }, []);

  const nextSlide = useCallback((index: number, bannersData: BannerProps[]) => {
    const newIndex = index === bannersData.length - 1 ? 0 : index + 1;
    setCurrentIndex(newIndex);
    setCurrentBannerTime(bannersData[newIndex].time);
  }, []);

  const getBanners = useCallback(async () => {
    try {
      const response = await api.get('/banners');
      setBanners(response.data);
      setCurrentBannerTime(
        response.data.length > 0 ? response.data[0].time : 4,
      );
      setCurrentIndex(response.data.length > 0 ? 0 : -1);
    } catch {
      // Handle error appropriately
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (banners.length > 0 && !mouseHover) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        () => nextSlide(currentIndex, banners),
        currentBannerTime * 1000,
      );
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [nextSlide, currentIndex, banners, currentBannerTime, mouseHover]);

  useEffect(() => {
    getBanners();
  }, [getBanners]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Em ecrãs táteis, o browser dispara um mouseenter sintético a seguir ao
    // toque (compatibilidade com sites que só ouvem eventos de rato) — sem
    // um rato real para disparar o mouseleave correspondente, mouseHover
    // ficava preso a true para sempre, pausando a rotação automática depois
    // do primeiro toque. Repor aqui explicitamente.
    setMouseHover(false);

    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 48) {
      if (delta > 0) nextSlide(currentIndex, banners);
      else prevSlide(currentIndex, banners);
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <Skeleton
        w="100%"
        borderRadius="24px"
        style={{ aspectRatio: '2 / 1' }}
      />
    );
  }

  if (banners.length === 0) return null;

  return (
    <Flex
      pos="relative"
      align="center"
      justify="center"
      overflow="hidden"
      onMouseEnter={() => setMouseHover(true)}
      onMouseLeave={() => setMouseHover(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      borderRadius="24px"
      boxShadow="0 8px 40px rgba(0,176,202,0.12), 0 2px 8px rgba(0,0,0,0.08)"
    >
      {/* Slides */}
      <Flex
        width="full"
        transition="transform 0.55s cubic-bezier(0.4,0,0.2,1)"
        transform={`translateX(${-currentIndex * 100}%)`}
        align="stretch"
      >
        {banners.map((banner, index) => (
          // width (não só minWidth) é necessário: com flexShrink=0 e sem
          // width explícito, um item flex cresce para caber o conteúdo —
          // se a imagem lá dentro for maior que o ecrã (ex: 2000px numa
          // imagem que devia ser "mobile"), o slide inteiro crescia para
          // 2000px, fazendo o translateX(-100%) deslizar dentro da mesma
          // imagem em vez de mudar para o slide seguinte.
          <Link
            href={banner.link}
            key={banner.id}
            style={{
              width: '100%',
              minWidth: '100%',
              flexShrink: 0,
              display: 'block',
            }}
          >
            {/* Mobile image — o backoffice pede uma imagem própria 16:9
                para este slot ("Banner (Mobile) - 16:9"); o contentor tem de
                respeitar esse rácio, senão a imagem fica mal enquadrada. */}
            <Box
              display={{ base: 'block', md: 'none' }}
              w="100%"
              overflow="hidden"
              style={{ aspectRatio: '16 / 9' }}
            >
              <Image
                src={banner.path_mobile || banner.path_desktop || ''}
                alt={`Banner ${index + 1}`}
                w="100%"
                h="100%"
                style={{ objectFit: 'cover', display: 'block' }}
              />
            </Box>
            {/* Desktop image — o backoffice pede 2:1 ("Banner (DeskTop) -
                2:1"). Antes só havia maxH="500px" sem aspect-ratio fixo, o
                que faz o rácio real do contentor variar com a largura do
                ecrã (ex: 1280px de largura dava 2.56:1, não 2:1),
                desalinhando o crop do object-fit:cover e cortando conteúdo
                (ex: o rodapé da imagem). */}
            <Box
              display={{ base: 'none', md: 'block' }}
              w="100%"
              overflow="hidden"
              style={{ aspectRatio: '2 / 1' }}
            >
              <Image
                src={banner.path_desktop || ''}
                alt={`Banner ${index + 1}`}
                w="100%"
                h="100%"
                style={{ objectFit: 'cover', display: 'block' }}
              />
            </Box>
          </Link>
        ))}
      </Flex>

      {/* Dot navigation */}
      {banners.length > 1 && (
        <Flex
          pos="absolute"
          bottom="14px"
          left="50%"
          transform="translateX(-50%)"
          gap="6px"
          zIndex="3"
          align="center"
        >
          {banners.map((_, i) => (
            <Box
              key={i}
              w={i === currentIndex ? '22px' : '8px'}
              h="8px"
              borderRadius="full"
              bg={i === currentIndex ? '#00B0CA' : 'rgba(255,255,255,0.55)'}
              cursor="pointer"
              transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
              boxShadow={
                i === currentIndex ? '0 0 8px rgba(0,176,202,0.6)' : 'none'
              }
              onClick={() => {
                setCurrentIndex(i);
                setCurrentBannerTime(banners[i].time);
              }}
            />
          ))}
        </Flex>
      )}

      {/* Desktop arrows — show on hover */}
      {mouseHover && banners.length > 1 && (
        <>
          <ArrowButton
            onClick={() => prevSlide(currentIndex, banners)}
            direction="left"
          />
          <ArrowButton
            onClick={() => nextSlide(currentIndex, banners)}
            direction="right"
          />
        </>
      )}

      {/* Mobile arrows — always visible */}
      {banners.length > 1 && (
        <Box display={{ base: 'block', md: 'none' }} opacity={0.75}>
          <ArrowButton
            onClick={() => prevSlide(currentIndex, banners)}
            direction="left"
          />
          <ArrowButton
            onClick={() => nextSlide(currentIndex, banners)}
            direction="right"
          />
        </Box>
      )}
    </Flex>
  );
};

export default BannerCarousel;



